import { LeadFormVisibilityMode } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@up2stay/up2promo-ui';
import { Switch } from '@up2stay/up2promo-ui';
import { Minus, Plus } from 'lucide-react';
import type { UseFormSetValue } from 'react-hook-form';

import { ProfileEditFieldOverrideHint } from '@/core/components/profile/edit/profile-edit-field-override-hint';
import type { ProfileFormData } from '@/core/components/profile/edit/profile-edit-form-schema';
import { ProfileFieldLockedHint } from '@/core/components/profile-templates/profile-field-locked-hint';
import {
  SettingsGroup,
  SettingsGroupFooter,
  SettingsGroupLabel,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import type { ProfileTemplateFieldKey } from '@/core/lib/profile-template-fields';

type LeadFormDict = {
  title: string;
  description: string;
  enabled: string;
  enabledDescription: string;
  visibilityModeLabel: string;
  visibilityModeDescription: string;
  visibilityMode: {
    manual: string;
    autoOpen: string;
  };
  autoOpenDelayLabel?: string;
  autoOpenDelayDescription?: string;
};

type LeadsWorkspaceDict = {
  title: string;
  description: string;
  toggleLabel: string;
};

type ProfileEditLeadFormSectionProps = {
  leadFormDict: LeadFormDict;
  leadsWorkspaceDict: LeadsWorkspaceDict;
  formValues: ProfileFormData;
  setValue: UseFormSetValue<ProfileFormData>;
  isLocked: (key: ProfileTemplateFieldKey) => boolean;
  fieldLockedHintMessage: string;
  overrideHintMessage: string;
  resetLabel: string;
  templateValueLabel: string;
  getFieldOverrideDisplay: Parameters<
    typeof ProfileEditFieldOverrideHint
  >[0]['getFieldOverrideDisplay'];
  onResetField: (key: ProfileTemplateFieldKey) => void;
  showLeadsWorkspaceCard: boolean;
};

export function ProfileEditLeadFormSection({
  leadFormDict,
  leadsWorkspaceDict,
  formValues,
  setValue,
  isLocked,
  fieldLockedHintMessage,
  overrideHintMessage,
  resetLabel,
  templateValueLabel,
  getFieldOverrideDisplay,
  onResetField,
  showLeadsWorkspaceCard,
}: ProfileEditLeadFormSectionProps) {
  const overrideHintProps = {
    getFieldOverrideDisplay,
    overrideHintMessage,
    resetLabel,
    templateValueLabel,
    onReset: onResetField,
  };

  return (
    <div className="flex min-w-0 w-full flex-col gap-6">
      <div className="flex min-w-0 flex-col gap-2">
        <SettingsGroupLabel>{leadFormDict.title}</SettingsGroupLabel>
        <SettingsGroup>
          <SettingsGroupRows>
            <SettingsGroupRow>
              <div className="flex items-center justify-between gap-4">
                <div className="flex min-w-0 flex-1 items-center gap-0.5">
                  <Label htmlFor="leadFormEnabled">
                    {leadFormDict.enabled}
                  </Label>
                  <SettingsInfoHint label={leadFormDict.enabled}>
                    {leadFormDict.enabledDescription}
                  </SettingsInfoHint>
                </div>
                <Switch
                  id="leadFormEnabled"
                  checked={formValues.leadFormEnabled ?? false}
                  disabled={isLocked('leadForm')}
                  onCheckedChange={(checked) =>
                    setValue('leadFormEnabled', !!checked)
                  }
                />
              </div>
            </SettingsGroupRow>

            {formValues.leadFormEnabled ? (
              <>
                <SettingsGroupRow separated>
                  <div className="space-y-0.5 min-w-0">
                    <div className="flex items-center gap-0.5">
                      <Label htmlFor="leadFormVisibilityMode">
                        {leadFormDict.visibilityModeLabel}
                      </Label>
                      <SettingsInfoHint
                        label={leadFormDict.visibilityModeLabel}
                      >
                        {leadFormDict.visibilityModeDescription}
                      </SettingsInfoHint>
                    </div>
                    <Select
                      value={
                        formValues.leadFormVisibilityMode === 'off'
                          ? 'manual'
                          : (formValues.leadFormVisibilityMode ?? 'manual')
                      }
                      disabled={isLocked('leadForm')}
                      onValueChange={(value) =>
                        setValue(
                          'leadFormVisibilityMode',
                          value as 'manual' | 'auto_open',
                        )
                      }
                    >
                      <SelectTrigger
                        id="leadFormVisibilityMode"
                        className="min-w-0 w-full [&>span:first-child]:pl-0 [&>span:first-child]:text-left [&>span:first-child]:min-w-0 [&>span:first-child]:truncate"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={LeadFormVisibilityMode.MANUAL}>
                          {leadFormDict.visibilityMode.manual}
                        </SelectItem>
                        <SelectItem value={LeadFormVisibilityMode.AUTO_OPEN}>
                          {leadFormDict.visibilityMode.autoOpen}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </SettingsGroupRow>

                {formValues.leadFormVisibilityMode === 'auto_open' ? (
                  <SettingsGroupRow separated>
                    <div className="space-y-0.5 pt-0.5">
                      <div className="flex items-center gap-0.5">
                        <Label htmlFor="leadFormAutoOpenDelay">
                          {leadFormDict.autoOpenDelayLabel || 'Auto-open delay'}
                        </Label>
                        <SettingsInfoHint
                          label={
                            leadFormDict.autoOpenDelayLabel || 'Auto-open delay'
                          }
                        >
                          {leadFormDict.autoOpenDelayDescription ||
                            'Time in seconds before the form is shown automatically.'}
                        </SettingsInfoHint>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 shrink-0"
                          onClick={() => {
                            const current =
                              formValues.leadFormAutoOpenDelay ?? 0;
                            setValue(
                              'leadFormAutoOpenDelay',
                              Math.max(0, current - 1),
                            );
                          }}
                          disabled={
                            isLocked('leadForm') ||
                            (formValues.leadFormAutoOpenDelay ?? 0) <= 0
                          }
                        >
                          <Minus className="h-4 w-4" />
                          <span className="sr-only">Verringern</span>
                        </Button>
                        <Input
                          id="leadFormAutoOpenDelay"
                          type="number"
                          min={0}
                          max={15}
                          step={1}
                          value={formValues.leadFormAutoOpenDelay ?? 0}
                          readOnly
                          className="w-20 text-center cursor-default [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          style={{ textAlign: 'center' }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-10 w-10 shrink-0"
                          onClick={() => {
                            const current =
                              formValues.leadFormAutoOpenDelay ?? 0;
                            setValue(
                              'leadFormAutoOpenDelay',
                              Math.min(15, current + 1),
                            );
                          }}
                          disabled={
                            isLocked('leadForm') ||
                            (formValues.leadFormAutoOpenDelay ?? 0) >= 15
                          }
                        >
                          <Plus className="h-4 w-4" />
                          <span className="sr-only">Erhöhen</span>
                        </Button>
                      </div>
                    </div>
                  </SettingsGroupRow>
                ) : null}
              </>
            ) : null}

            <div className="px-4 pb-3 sm:px-5">
              <ProfileFieldLockedHint
                locked={isLocked('leadForm')}
                message={fieldLockedHintMessage}
              />
              <ProfileEditFieldOverrideHint
                fieldKey="leadForm"
                {...overrideHintProps}
              />
            </div>
          </SettingsGroupRows>
        </SettingsGroup>
        <SettingsGroupFooter>{leadFormDict.description}</SettingsGroupFooter>
      </div>

      {showLeadsWorkspaceCard ? (
        <div className="flex min-w-0 flex-col gap-2">
          <SettingsGroupLabel>{leadsWorkspaceDict.title}</SettingsGroupLabel>
          <SettingsGroup>
            <SettingsGroupRow>
              <div className="flex items-center justify-between gap-4">
                <Label
                  htmlFor="leadsAutoShareWithWorkspace"
                  className="cursor-pointer flex-1 min-w-0 leading-snug text-sm font-normal"
                >
                  {leadsWorkspaceDict.toggleLabel}
                </Label>
                <Switch
                  id="leadsAutoShareWithWorkspace"
                  checked={formValues.leadsAutoShareWithWorkspace ?? false}
                  disabled={isLocked('leadsAutoShareWithWorkspace')}
                  onCheckedChange={(checked) =>
                    setValue('leadsAutoShareWithWorkspace', !!checked)
                  }
                />
              </div>
              <ProfileFieldLockedHint
                locked={isLocked('leadsAutoShareWithWorkspace')}
                message={fieldLockedHintMessage}
              />
              <ProfileEditFieldOverrideHint
                fieldKey="leadsAutoShareWithWorkspace"
                {...overrideHintProps}
              />
            </SettingsGroupRow>
          </SettingsGroup>
          <SettingsGroupFooter>
            {leadsWorkspaceDict.description}
          </SettingsGroupFooter>
        </div>
      ) : null}
    </div>
  );
}
